import { useMutation } from '@tanstack/react-query';
import { getBFFApiClient } from '@acp/feature/auth';
import { useToast } from '@chakra-ui/react';
import { Quote } from '@acp/shared';

export function useQuotePdfDownload({
    onSuccess = () => {},
    onError = () => {},
}: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
} = {}) {
    const toast = useToast();

    const mutation = useMutation({
        mutationFn: async ({
            quote,
        }: {
            quote: Quote;
        }) => {
            toast({
                title: 'We\'re preparing your quote',
                description: 'Please wait while we prepare your quote for download.',
                status: 'info',
            });
            const res = await getBFFApiClient().get<Blob>(quote.pdfUrl, {
                responseType: 'blob',
            });

            if (res.status !== 200) {
                throw new Error('Failed to download quote PDF');
            }

            return URL.createObjectURL(res.data);
        },
        onSuccess: (url, { quote }) => {
            // We are breaking React's rules here by directly manipulating the DOM
            // it is OK, so long as we clean up after ourselves
            const link = document.createElement('a');
            link.href = url;
            link.download = `quote-${quote.quoteNumber}.pdf`;
            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast({
                title: `Quote ${quote.quoteNumber} downloaded`,
                description: 'Please check your downloads folder for the quote.',
                status: 'success',
                isClosable: true,
            });

            onSuccess();
        },
        onError: (error, { quote }) => {
            toast({
                title: `Quote ${quote.quoteNumber} failed to download`,
                description: 'We experienced an error while trying to download the quote. Please try again.',
                status: 'error',
                isClosable: true,
            });
            onError(error);
        },
    });

    return {
        isDownloading: mutation.isPending,
        download: mutation.mutate,
    };
}
